import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useJsonForms } from "@jsonforms/react";
import type { ControlProps } from "@jsonforms/core";
import { useCaseFieldReview } from "../../shared/hooks/useCaseFieldReview";
import { toCatalogApiError } from "../../shared/utils/catalogErrors";
import type { CaseFieldValues } from "../../shared/model/caseReview";
import { imeSeriousnessFor } from "../services/imeSeriousnessService";
import { IME_SERIOUSNESS_QUERY_KEY, IME_SERIOUSNESS_TEXT } from "../constants/imeSeriousnessLabels";
import { confirmationLines, imeTargetKey, imeTargetOf, type ImeConfirmation } from "../model/imeSeriousness";
import { useImeSeriousnessSyncContext, type ImeRowStatus } from "./useImeSeriousnessSync";

export interface ImeSeriousnessControl {
  field: ReturnType<typeof useCaseFieldReview>;
  /** Whether the switch is on for this event. */
  on: boolean;
  /** Whether this screen can apply the list; the Case Review workspace can, a screen without it shows the state. */
  available: boolean;
  /** The confirmation while it is open; null while it is closed. */
  confirmation: ImeConfirmation | null;
  /** What the list did, or is doing, after a change of the Preferred Term while the switch is on. */
  status: ImeRowStatus | undefined;
  /** The reviewer turns the switch: on opens the confirmation, off changes only the switch. */
  turn: (on: boolean) => void;
  /** Applies what the list gives to the form and turns the switch on. The save stores it. */
  accept: () => void;
  /** Closes the confirmation; the switch stays off and nothing changes. */
  cancel: () => void;
}

/**
 * "Derive seriousness from the EMA IME list" of one event (product owner, 25 Oct 2026).
 *
 * Turning it on opens a confirmation that names the list version and what happens to this event. Accept applies the
 * list to the event's preferred term (PT): on the list, only Medically Significant and the system sentence; not on the
 * list, no criterion and empty notes; no PT, nothing. The values change in the form as reviewer edits; the save
 * stores them. Later changes of the PT while the switch is on go through {@link useImeSeriousnessSync}. Turning the
 * switch off changes only the switch.
 */
export function useImeSeriousness(props: ControlProps): ImeSeriousnessControl {
  const field = useCaseFieldReview(props);
  const sync = useImeSeriousnessSyncContext();
  const form = useJsonForms();
  const recordId = field.context.recordId;
  const [confirming, setConfirming] = useState(false);
  const target = imeTargetOf((form.core?.data ?? {}) as CaseFieldValues);
  const preview = useQuery({
    queryKey: [IME_SERIOUSNESS_QUERY_KEY, imeTargetKey(target)],
    enabled: confirming,
    retry: false,
    staleTime: 10 * 60_000,
    queryFn: () => imeSeriousnessFor(target),
  });

  const confirmation: ImeConfirmation | null = !confirming ? null
    : preview.isError ? { status: "failed", message: toCatalogApiError(preview.error).message || IME_SERIOUSNESS_TEXT.failed }
    : preview.data === undefined ? { status: "loading", message: IME_SERIOUSNESS_TEXT.loading }
    : { status: "ready", lines: confirmationLines(preview.data), preview: preview.data };

  return {
    field,
    on: props.data === true,
    available: sync !== undefined && recordId !== undefined,
    confirmation,
    status: sync && recordId !== undefined ? sync.statusOf(recordId) : undefined,
    turn: (on) => {
      if (!sync || recordId === undefined) return;
      if (on) { setConfirming(true); return; }
      sync.forget(recordId);
      field.update(false);
    },
    accept: () => {
      if (confirmation?.status !== "ready" || !sync || recordId === undefined) return;
      sync.accept(recordId, target, confirmation.preview);
      field.update(true);
      setConfirming(false);
    },
    cancel: () => setConfirming(false),
  };
}
